import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type AdvancedFiltersConfig,
  AdvancedFiltersContextProvider,
  type AdvancedFiltersContextProviderRef,
  type InferAdvancedFilterPublicState,
} from '../../advanced-filters';
import { areAdvancedFiltersEqual } from '../utilities/FiltersDrawer.utils';

type AdvancedFiltersStateContextValue<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> = {
  hasAdvancedFilters: boolean;
  isAdvancedFiltersDirty: boolean;
  syncFromExternalAdvancedState: () => void;
  localAdvancedFilters: InferAdvancedFilterPublicState<Config> | null;
  flushAdvancedFiltersState: (
    filters: InferAdvancedFilterPublicState<Config> | null,
  ) => void;
  selectedAdvancedFilters: InferAdvancedFilterPublicState<Config> | null;
};

const AdvancedFiltersStateContext =
  createContext<AdvancedFiltersStateContextValue | null>(null);

interface AdvancedFiltersStateProviderProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends PropsWithChildren {
  advancedFiltersConfig: Config;
  selectedAdvancedFilters: InferAdvancedFilterPublicState<Config> | null;
}

export const AdvancedFiltersStateProvider = <
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
>({
  children,
  advancedFiltersConfig,
  selectedAdvancedFilters,
}: AdvancedFiltersStateProviderProps<Config>) => {
  const [localAdvancedFilters, setLocalAdvancedFilters] = useState(
    selectedAdvancedFilters,
  );

  const hasAdvancedFilters = useMemo(
    () => Boolean(localAdvancedFilters?.conditions?.length),
    [localAdvancedFilters],
  );

  const isAdvancedFiltersDirty = useMemo(
    () =>
      !areAdvancedFiltersEqual(localAdvancedFilters, selectedAdvancedFilters),
    [localAdvancedFilters, selectedAdvancedFilters],
  );

  const providerRef = useRef<AdvancedFiltersContextProviderRef>(null);

  const flushAdvancedFiltersState = useCallback(
    (filters: InferAdvancedFilterPublicState<AdvancedFiltersConfig> | null) => {
      if (!providerRef.current) return;
      if (!filters || !filters.conditions.length) {
        providerRef.current.reset();
        return;
      }
      providerRef.current.flushNewAdvancedFilters(filters);
    },
    [],
  );

  const syncFromExternalAdvancedState = useCallback(() => {
    flushAdvancedFiltersState(selectedAdvancedFilters);
    setLocalAdvancedFilters(selectedAdvancedFilters);
  }, [flushAdvancedFiltersState, selectedAdvancedFilters]);

  const providerValue = useMemo(
    () => ({
      hasAdvancedFilters,
      isAdvancedFiltersDirty,
      syncFromExternalAdvancedState,
      localAdvancedFilters: localAdvancedFilters,
      flushAdvancedFiltersState,
      selectedAdvancedFilters,
    }),
    [
      hasAdvancedFilters,
      isAdvancedFiltersDirty,
      syncFromExternalAdvancedState,
      localAdvancedFilters,
      flushAdvancedFiltersState,
      selectedAdvancedFilters,
    ],
  );

  useEffect(() => {
    syncFromExternalAdvancedState();
  }, [selectedAdvancedFilters, syncFromExternalAdvancedState]);

  return (
    <AdvancedFiltersStateContext.Provider value={providerValue}>
      <AdvancedFiltersContextProvider
        ref={providerRef}
        advancedFiltersConfig={advancedFiltersConfig}
        defaultAdvancedFilters={localAdvancedFilters ?? undefined}
        onAdvancedFiltersChange={setLocalAdvancedFilters}
      >
        {children}
      </AdvancedFiltersContextProvider>
    </AdvancedFiltersStateContext.Provider>
  );
};

export const useAdvancedFiltersStateContext = <
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
>() => {
  const context = useContext(AdvancedFiltersStateContext);
  if (!context) {
    throw new Error(
      'useAdvancedFiltersStateContext must be used within AdvancedFiltersStateProvider',
    );
  }
  return context as AdvancedFiltersStateContextValue<Config>;
};
